/* ────────────────────────────────────────────────────────
   The columnist roster.

   Each writer's article list is their REAL one, taken from their author page on
   ejinsight.com — the six pages QA gave as the reference:

     Michael Chugani   /eji/author/id/7463
     Stephen Vines     /eji/author/id/8129
     Frank Ching       /eji/author/id/218
     Mark O'Neill      /eji/author/id/206
     Neville Sarony    /eji/author/id/8912
     Brian Y. S. Wong  /eji/author/id/12907

   Before this, a page was COMPOSED by scanning the section pools for rows
   carrying the writer's byline, which gave most of them one article and none of
   them more than a handful. QA: "add more articles to each columnist".

   Two things are the fixtures' own rather than the source's: the years are
   shifted onto this demo's 2025–2026 timeline so the ordering matches
   everything else on the site, and the thumbnails cycle the section imagery,
   because the real pages' images are not in this repo.

   The roster is SIX, which is what the reference lists and what
   `columnistImages` already carried — with one correction: the fifth writer is
   Neville Sarony, not "Neille". The avatar file keeps its old name.
   ──────────────────────────────────────────────────────── */
export const COLUMNIST_ROSTER: {
  name: string;
  avatar: string;
  role: string;
  bio: string;
  /** The author id on ejinsight.com, kept so the source of each list is
   *  traceable from the fixture. */
  sourceId: string;
  articles: { title: string; date: string; img: string }[];
}[] = [
  {
    name: "Michael Chugani",
    avatar: "/images/ejinsight/columnist-michael-chugani.png",
    role: "Politics & Society",
    bio: "A Hong Kong-born American citizen who has worked for many years as a journalist in Hong Kong, the USA and London.",
    sourceId: "7463",
    articles: [
      {
        title: "How should leaders govern the people?",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "America at 250: A bitterly divided nation",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "The Trump-Mamdani Bromance",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Does Age Matter in Politics?",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "Should HK limit overtourism?",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "Is America a broken nation?",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "Trump's Remaking of America",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Democracy is dying in Trump's America",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "Jimmy Carter, Donald Trump, and China",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "President Trump or King Trump?",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "America: A Nation Divided",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "Hong Kong: A Tale of Three Cities?",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
  {
    name: "Stephen Vines",
    avatar: "/images/ejinsight/columnist-stephen-vines.png",
    role: "Hong Kong Affairs",
    bio: "A Hong Kong-based journalist, broadcaster and book author.",
    sourceId: "8129",
    articles: [
      {
        title: "Hong Kong's unique scheme for subsidising the rich",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "How to turn the pandemic into utter misery for the less well off",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "No one who expects to win an election wants it postponed",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Beijing's best shot — we are as vile as you",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "One kowtow is never enough",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "Why the government doesn't want the virus to go away",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "Put the theme parks out of their misery",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Politicising the coronavirus crisis",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "We are not all in this together!",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "A big, big apology is due to the people of Hong Kong",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "What happens after the virus fades?",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "Useless hardly covers it for the Lam administration",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
  {
    name: "Frank Ching",
    avatar: "/images/ejinsight/columnist-frank-ching.png",
    role: "Greater China",
    bio: "Opened The Wall Street Journal's bureau in China in 1979. A Hong Kong-based writer on Chinese affairs.",
    sourceId: "218",
    articles: [
      {
        title: "Does Hong Kong have the right not to enact article 23 laws?",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "A proposal for the consideration of Taiwan's president-elect",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "Hong Kong needs to de-emphasize national security",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Thaw in U.S.-China ties driven by domestic issues",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "Resolving Hong Kong conundrum key to US-China summit at Apec",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "Applying Chinese wisdom to resolve the Taiwan issue",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "China, doubling BRICS size, challenges U.S.-led global order",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Hong Kong needs to protect its image as financial center",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "China-U.S. relations: Is it possible to stop the slide?",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "How fares Hong Kong 3 years after China's crackdown?",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "Ukraine and Taiwan: Can China square the circle?",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "After Biden, Xi agree to fix ties, aides shouldn't get in the way",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
  {
    name: "Mark O'Neill",
    avatar: "/images/ejinsight/columnist-mark-oneill.png",
    role: "World Affairs",
    bio: "A Hong Kong-based writer, teacher and speaker.",
    sourceId: "206",
    articles: [
      {
        title: "China's rich tremble at taxes, exit bans",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "In Russia, Chinese workers have privilege to protest",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "Taiwan to post highest GDP growth in 39 years",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Beijing may cancel September summit with Trump",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "What do North Korean soldiers get from the war?",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "EU becomes largest foreign investor in Taiwan",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "Ireland becomes semiconductor hub in Europe",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Putin prepares Forever War",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "China-Taiwan war would cost US$2 trillion",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "Will BYD replace Toyota as top global automaker?",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "Will French vote for convict as President?",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "China wreaks its economic revenge on Europe",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
  {
    name: "Neville Sarony",
    avatar: "/images/ejinsight/columnist-neille-sarony.png",
    role: "Law & Justice",
    bio: "King's Counsel, writing on the law, justice and the courts.",
    sourceId: "8912",
    articles: [
      {
        title: "Rank dishonour",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "Nothing changes",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "Sanctimonious sanctions",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Clueless in Gaza",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "Contempt for Justice",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "Colour blind",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "The power breakers",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Healing the mind",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "Heat therapy",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "Why not put it on ice?",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "'Brahn boots'",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "Counsel of choice",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
  {
    name: "Brian Y. S. Wong",
    avatar: "/images/ejinsight/columnist-brian-wong.png",
    role: "Public Policy",
    bio: "Assistant Professor at the University of Hong Kong, writing on ethics and public policy.",
    sourceId: "12907",
    articles: [
      {
        title: "Manufacturing Failure",
        date: "August 31, 2026",
        img: "/images/ejinsight/hk-1.png",
      },
      {
        title: "Manufacturing Success",
        date: "August 29, 2026",
        img: "/images/ejinsight/world-1.png",
      },
      {
        title: "In search of ambition",
        date: "August 27, 2026",
        img: "/images/ejinsight/business-1.png",
      },
      {
        title: "Is certainty a sin?",
        date: "August 25, 2026",
        img: "/images/ejinsight/features-1.png",
      },
      {
        title: "The trinity of ethical tech governance",
        date: "August 23, 2026",
        img: "/images/ejinsight/lifestyle-1.png",
      },
      {
        title: "History lives on in shades of grey",
        date: "August 21, 2026",
        img: "/images/ejinsight/hk-2.png",
      },
      {
        title: "Why Carpe Diem",
        date: "August 19, 2026",
        img: "/images/ejinsight/world-2.png",
      },
      {
        title: "Surviving the era of automation",
        date: "August 17, 2026",
        img: "/images/ejinsight/business-2.png",
      },
      {
        title: "King Lear or King Loser",
        date: "June 12, 2026",
        img: "/images/ejinsight/hk-3.png",
      },
      {
        title: "The act of writing",
        date: "April 3, 2026",
        img: "/images/ejinsight/world-3.png",
      },
      {
        title: "For Whom Do We Live",
        date: "January 22, 2026",
        img: "/images/ejinsight/business-3.png",
      },
      {
        title: "What does it take to 'build character'?",
        date: "November 5, 2025",
        img: "/images/ejinsight/lifestyle-2.png",
      },
    ],
  },
];
